/**
 * ModalCustomizationSelector.jsx
 * ─────────────────────────────────────────────────────────
 * MIDDLE SECTION – Product customization groups and options.
 *
 * Renders customization groups (e.g. "Add-ons", "Bread type")
 * with their options as selectable chips.
 *
 * Selection Rules:
 *  - Radio-style: if maxSelections === 1
 *  - Checkbox-style: if maxSelections > 1
 *  - Each option shows its price addition (e.g. + 5.00 EGP)
 *
 * Box Style: If groupName contains "box", uses box-style chips
 * Spicy Style: If optionName contains "spicy", shows spicy styling
 *
 * @param {object}   props
 * @param {Array}    props.customizationGroups - Array of customization groups
 * @param {object}   props.selectedCustomizations - { [groupId]: [optionId1, ...] }
 * @param {Function} props.onCustomizationChange - (groupId, optionId, isSelected) => void
 */
/* eslint-disable react/prop-types */
import React from 'react';
import { useLanguage } from '@components/common/locale/useLanguage';
import { _ } from '@evershop/evershop/src/lib/locale/translate';
var isCustomizationGroupRequired = group => (group === null || group === void 0 ? void 0 : group.isRequired) === true || (group === null || group === void 0 ? void 0 : group.isRequired) === 'true' || (group === null || group === void 0 ? void 0 : group.isRequired) === 1 || (group === null || group === void 0 ? void 0 : group.isRequired) === '1';
export default function ModalCustomizationSelector(_ref) {
  var {
    customizationGroups,
    selectedCustomizations,
    onCustomizationChange
  } = _ref;
  var {
    isAr
  } = useLanguage();
  if (!(customizationGroups !== null && customizationGroups !== void 0 && customizationGroups.length)) return null;
  var sortedGroups = [...customizationGroups].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  return /*#__PURE__*/React.createElement("div", {
    className: "modal-customizations"
  }, sortedGroups.map(group => {
    var sortedOptions = [...(group.options || [])].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    if (!sortedOptions.length) return null;
    var selectedIds = selectedCustomizations[group.customizationGroupId] || [];
    var selectionCount = selectedIds.length;
    var maxSelections = Number(group.maxSelections) || 0;
    var isRequired = isCustomizationGroupRequired(group);
    var reachedMax = maxSelections > 0 && selectionCount >= maxSelections;
    var isBoxStyle = /(bucket|box\s*size)/i.test(group.groupName);
    return /*#__PURE__*/React.createElement("div", {
      key: group.customizationGroupId,
      className: "modal-variant-group"
    }, /*#__PURE__*/React.createElement("div", {
      className: "modal-variant-header"
    }, /*#__PURE__*/React.createElement("div", {
      className: "modal-variant-titles"
    }, /*#__PURE__*/React.createElement("p", {
      className: "modal-variant-label"
    }, isAr ? group.groupNameAr || group.groupName : group.groupName), /*#__PURE__*/React.createElement("p", {
      className: "modal-variant-subtitle"
    }, isAr ? group.groupDescriptionAr || group.groupDescription : group.groupDescription)), isRequired && /*#__PURE__*/React.createElement("span", {
      className: "modal-variant-required-badge"
    }, _('Required')), !isRequired && /*#__PURE__*/React.createElement("span", {
      className: "modal-variant-optional-badge"
    }, _('Optional'))), /*#__PURE__*/React.createElement("div", {
      className: isBoxStyle ? 'modal-variant-box-grid md:px-[4rem]' : 'modal-variant-list flex flex-col items-center gap-6 mt-9 mb-9'
    }, sortedOptions.map((option, optionIndex) => {
      var isSelected = selectedIds.includes(String(option.customizationOptionId));
      var isSelectable = !reachedMax || isSelected || maxSelections === 1;
      var optionText = option.optionName || '';
      var optionTextLower = optionText.toLowerCase();
      var isSpicy = optionTextLower.includes('spicy');
      var hasImage = !!option.optionImage;

      // Progressive scale for box-style size groups (e.g. Bucket Size):
      // Desktop scale range: 1.7 (smallest) to 2.6 (biggest)
      // Mobile scale range: 1.2 (smallest) to 1.7 (biggest)
      var count = sortedOptions.length;
      var baseScaleMd = 1.7;
      var scaleStepMd = 0.9;
      var fbsScaleMd = isBoxStyle && count > 1 ? baseScaleMd + optionIndex / (count - 1) * scaleStepMd : 1.3;
      var baseScaleSm = 1.2;
      var scaleStepSm = 0.5;
      var fbsScaleSm = isBoxStyle && count > 1 ? baseScaleSm + optionIndex / (count - 1) * scaleStepSm : 1.1;

      // Separate top position calculation for each bucket size to keep them vertically aligned
      var imgTopSm = isBoxStyle && count > 1 ? "".concat(-2.5 - optionIndex * 0.6, "rem") : undefined;
      var imgTopMd = isBoxStyle && count > 1 ? "".concat(-3.9 - optionIndex * 1, "rem") : undefined;
      if (isBoxStyle) {
        return /*#__PURE__*/React.createElement("button", {
          key: option.customizationOptionId,
          type: "button",
          disabled: !isSelectable && !isSelected,
          className: "modal-variant-box-chip".concat(isSelected ? ' modal-variant-box-chip--selected' : ''),
          onClick: () => onCustomizationChange(group.customizationGroupId, String(option.customizationOptionId), !isSelected),
          "aria-pressed": isSelected
        }, hasImage && /*#__PURE__*/React.createElement("div", {
          className: "modal-variant-box-image-wrapper",
          style: {
            '--img-top-sm': imgTopSm,
            '--img-top-md': imgTopMd
          }
        }, /*#__PURE__*/React.createElement("img", {
          src: option.optionImage,
          alt: option.optionName,
          className: "modal-variant-box-chip-image",
          style: {
            '--img-scale-sm': fbsScaleSm,
            '--img-scale-md': fbsScaleMd
          }
        })), /*#__PURE__*/React.createElement("div", {
          className: "modal-variant-box-content"
        }, /*#__PURE__*/React.createElement("div", {
          className: "modal-variant-box-indicator !self-start"
        }, isSelected && /*#__PURE__*/React.createElement("svg", {
          width: "10",
          height: "8",
          viewBox: "0 0 10 8",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg"
        }, /*#__PURE__*/React.createElement("path", {
          d: "M1 4L3.5 6.5L9 1",
          stroke: "#fff",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }))), /*#__PURE__*/React.createElement("div", {
          className: "modal-variant-box-details"
        }, /*#__PURE__*/React.createElement("span", {
          className: "modal-variant-box-chip-text"
        }, isAr ? option.optionNameAr || option.optionName : option.optionName), option.priceAddition > 0 && /*#__PURE__*/React.createElement("span", {
          className: "modal-variant-box-chip-price !font-rubik"
        }, "+ ", !isAr && 'EGP ', parseFloat(option.priceAddition).toFixed(2), isAr && /*#__PURE__*/React.createElement("span", {
          className: "!font-swissra"
        }, " \u062C\u0645")))));
      }
      return /*#__PURE__*/React.createElement("button", {
        key: option.customizationOptionId,
        type: "button",
        disabled: !isSelectable && !isSelected,
        className: "modal-variant-option-row bg-[#dbd9d938] ".concat(isSelected ? ' modal-variant-option-row--selected' : ''),
        onClick: () => onCustomizationChange(group.customizationGroupId, String(option.customizationOptionId), !isSelected),
        "aria-pressed": isSelected
      }, /*#__PURE__*/React.createElement("div", {
        className: "modal-variant-option-info"
      }, hasImage && /*#__PURE__*/React.createElement("div", {
        className: "flex items-center"
      }, /*#__PURE__*/React.createElement("img", {
        src: option.optionImage,
        alt: option.optionName,
        className: "modal-variant-option-image"
      })), /*#__PURE__*/React.createElement("div", {
        className: "flex flex-col items-start"
      }, /*#__PURE__*/React.createElement("span", {
        className: "modal-variant-option-text".concat(isSpicy ? ' modal-variant-option-text--spicy' : '')
      }, isAr ? option.optionNameAr || option.optionName : option.optionName, isSpicy && ' 🔥'), option.priceAddition > 0 && /*#__PURE__*/React.createElement("span", {
        className: "modal-variant-option-price !font-rubik"
      }, "+ ", !isAr && 'EGP ', parseFloat(option.priceAddition).toFixed(2), isAr && /*#__PURE__*/React.createElement("span", {
        className: "!font-swissra"
      }, " \u062C\u0645")))), /*#__PURE__*/React.createElement("div", {
        className: "modal-variant-radio-circle"
      }, isSelected && /*#__PURE__*/React.createElement("svg", {
        width: "28",
        height: "27",
        viewBox: "0 0 28 27",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg"
      }, /*#__PURE__*/React.createElement("rect", {
        width: "28",
        height: "27",
        rx: "13.5",
        fill: "#C60910"
      }), /*#__PURE__*/React.createElement("path", {
        d: "M8.98824 13.8801L11.5054 16.3601C11.8036 16.654 12.2828 16.653 12.5798 16.3579L18.9185 10.0615",
        stroke: "white",
        strokeWidth: "2.29151",
        strokeLinecap: "round"
      }))));
    })));
  }));
}